import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";

/**
 * Chart-shaped placeholders for the three report widgets while their figures are in flight.
 *
 * Why not the CircularProgress these replaced: a spinner alone in an empty box says "something is
 * happening somewhere" and nothing else. The card is 400px of blank surface, the reader cannot tell a
 * slow request from a broken one, and when the data lands the whole layout appears at once — Ingested
 * Data's series rail pushed the chart sideways as it arrived, because the rail only rendered once there
 * were series to list.
 *
 * A skeleton in the shape of the chart answers all of that: the card has its real size and its real
 * regions from the first frame, so nothing moves when the data replaces it, and the shape itself says
 * which card this is. This is the same treatment Media Metrics gives its tiles — see FigureSkeleton in
 * MediaMetrics.jsx — and the colour is deliberately identical so the three cards shimmer alike.
 *
 * "The shape itself" is meant literally, and is worth the trouble of a clip path: Ingested Data is a filled
 * area chart, so its placeholder is a filled area. It used to be a row of bars, which read as a different
 * card altogether and made the arrival of the real curve look like a redraw rather than a fill-in.
 *
 * All of these are decoration: `aria-hidden`, because each card already announces its loading state in
 * its own header, and a screen reader does not need thirteen shimmering rectangles read to it.
 */

/*
 * MUI derives Skeleton's colour from the palette, which is light in this app, so the default is very
 * nearly invisible against the near-black card surface. Same value as FigureSkeleton's.
 */
export const GRAPH_SKELETON_BG = 'rgba(255, 255, 255, 0.13)';

/*
 * The card surface the donuts are drawn on — DonutGraph passes this to Google Charts as
 * backgroundColor. Used here to punch the hole out of the middle of a filled circle, since MUI has no
 * ring variant and an actual ring would mean a border-only element that cannot shimmer.
 */
const CARD_SURFACE = '#3a3e3f';

/*
 * The plot's silhouette: height as a percentage of the plot area, sampled at even intervals across it.
 *
 * Ingested Data draws a Line with Filler registered, not a Bar — a cumulative curve with the area under it
 * filled (see waterfallGraph.jsx). A row of bars was therefore standing in for a shape the card never draws,
 * which is what was reported: the placeholder did not look like the thing it was replacing. This traces the
 * finished chart instead — a slow rise that steepens through the middle and plateaus once the back catalogue
 * is in, which is what a cumulative ingest actually looks like.
 *
 * Fixed rather than random: random would re-roll on every render, so the skeleton would twitch on each parent
 * update, and Math.random is not available to the workflow tooling that exercises this.
 */
const AREA_CURVE = [39, 43, 47, 51, 55, 58, 62, 65, 69, 74, 80, 86, 91, 95, 97.5, 98.8, 99.2, 99.3, 99.2, 99, 98.8];

/*
 * The curve above as a clip path, so one shimmering rectangle is cut to the area's outline.
 *
 * A polygon in percentages rather than `path()`, which takes absolute pixel coordinates and so would not
 * follow a card the reader resizes. Points run left to right along the curve, then down the right edge and
 * back along the bottom to close the fill under it.
 */
const AREA_CLIP_PATH = (() => {
    const step = 100 / (AREA_CURVE.length - 1);
    const alongTheCurve = AREA_CURVE.map((height, i) => `${(i * step).toFixed(2)}% ${(100 - height).toFixed(2)}%`);
    return `polygon(${[...alongTheCurve, '100% 100%', '0% 100%'].join(', ')})`;
})();

/*
 * The width kept clear down each side of the plot for an axis.
 *
 * Ingested Data draws two: `y` on the left titled 'Count', `y1` on the right titled 'Size (<unit>)', both
 * with 10px tick labels — see the scales block in waterfallGraph.jsx. Chart.js sizes each gutter at render
 * time from the widest tick label it ends up drawing, so an exact match is not available to a skeleton that
 * has no figures yet. These are sized to what the chart actually reserves for the magnitudes this card
 * shows (six-figure counts on the left, a two-figure size on the right), which is what stops the bars
 * spanning edge to edge and then jumping inwards when the real plot arrives.
 *
 * Both are drawn whether or not the series behind them turn up: a tape-only customer has no Files Count,
 * but it is Media Count and Data Size that own these two axes and neither of those is optional.
 */
const LEFT_AXIS_GUTTER = '4.5em';
const RIGHT_AXIS_GUTTER = '4.5em';

/** One shimmering block on the dark card surface. */
const Block = ({width, height, radius = 1, testId}) => (
    <Skeleton
        variant="rounded"
        aria-hidden="true"
        data-testid={testId}
        sx={{
            width,
            height,
            bgcolor: GRAPH_SKELETON_BG,
            borderRadius: radius,
        }}
    />
);

/**
 * One of the two value axes — a rotated title outboard of a column of tick labels.
 *
 * The title is a tall narrow block rather than turned text: a shimmer has no glyphs to rotate, and the
 * silhouette of a vertical bar beside the ticks is what reads as an axis title. Ticks are spread with
 * `space-around` rather than `space-between`, because Chart.js places the top and bottom ones inside the
 * plot's height, not flush against its edges.
 *
 * @param {'left'|'right'} side - which axis. Decides the gutter width and the order of the two parts, both
 *   of which mirror each other about the plot in the finished chart.
 */
const AxisGutter = ({side}) => (
    <Box
        aria-hidden="true"
        sx={{
            width: side === 'left' ? LEFT_AXIS_GUTTER : RIGHT_AXIS_GUTTER,
            flexShrink: 0,
            display: 'flex',
            // Puts the ticks nearest the plot on both sides, the title outboard of them.
            flexDirection: side === 'left' ? 'row' : 'row-reverse',
            alignItems: 'center',
            gap: 0.5,
            pr: side === 'left' ? 0.75 : 0,
            pl: side === 'right' ? 0.75 : 0,
        }}
    >
        <Block width="0.7em" height="4em" testId="axis-title-skeleton"/>
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-around',
                alignItems: side === 'left' ? 'flex-end' : 'flex-start',
                height: '100%',
                flexGrow: 1,
            }}
        >
            {[0, 1, 2, 3, 4].map(i => <Block key={i} width="2.4em" height="0.6em" testId="axis-tick-skeleton"/>)}
        </Box>
    </Box>
);

/**
 * An area chart that has not arrived — Ingested Data.
 *
 * @param {boolean} [showSeriesRail=false] - draw the placeholder for the series selector down the left.
 *   Reserving that width is what stops the chart jumping sideways when a rail arrives with the data, so
 *   it is worth having — but it has to match the caller, and a rail reserved for a chart that draws none
 *   causes the same jump in the other direction. Ingested Data used to carry one; it names its series in
 *   a legend under the plot now, so off is the default and no caller in the app asks for it.
 */
export const AreaChartSkeleton = ({showSeriesRail = false}) => (
    <Box
        data-testid="area-chart-skeleton"
        sx={{
            flexGrow: 1,
            display: 'flex',
            width: '100%',
            minHeight: 0,
        }}
    >
        {showSeriesRail &&
            <Box
                component="aside"
                aria-hidden="true"
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.75,
                    pr: 1,
                    pt: 1,
                    // Matches the real rail's divider in waterfallGraph.jsx, so the seam does not move.
                    borderRight: '1px solid #4a5058',
                }}
            >
                {[0, 1, 2].map(i => <Block key={i} width="4.5em" height="1.4em" testId="series-rail-skeleton"/>)}
            </Box>}
        <Box sx={{flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0, pl: showSeriesRail ? 1 : 0}}>
            {/* The plot band: an axis gutter down each side with the bars between them, so the bars begin and
                end where the real plot does instead of at the card's edges — which is what used to make the
                whole chart appear to slide inwards as the figures landed. */}
            <Box sx={{flexGrow: 1, display: 'flex', minHeight: 0, pt: 1}}>
                <AxisGutter side="left"/>
                {/* The filled area, as one shimmer clipped to the curve. Drawn as a single element rather
                    than a stack of segments because that is how it arrives — one dataset with a fill under
                    it — and because a clip path keeps the outline smooth at any width the card is given. */}
                <Box sx={{flexGrow: 1, minWidth: 0, minHeight: 0}}>
                    <Skeleton
                        variant="rectangular"
                        aria-hidden="true"
                        data-testid="area-fill-skeleton"
                        sx={{
                            width: '100%',
                            height: '100%',
                            bgcolor: GRAPH_SKELETON_BG,
                            borderRadius: 0,
                            clipPath: AREA_CLIP_PATH,
                        }}
                    />
                </Box>
                <AxisGutter side="right"/>
            </Box>
            {/* The category axis and its date labels, inset to the plot by the same two gutters: the x axis
                spans the plot area, not the card. Spacers rather than another AxisGutter, since there is
                nothing to draw below an axis title. */}
            <Box sx={{display: 'flex'}}>
                <Box aria-hidden="true" sx={{width: LEFT_AXIS_GUTTER, flexShrink: 0}}/>
                <Box sx={{flexGrow: 1, minWidth: 0}}>
                    <Block width="100%" height="2px" radius={0}/>
                    <Box sx={{display: 'flex', justifyContent: 'space-between', pt: 0.75}}>
                        {[0, 1, 2, 3].map(i => <Block key={i} width="4em" height="0.7em" testId="x-tick-skeleton"/>)}
                    </Box>
                </Box>
                <Box aria-hidden="true" sx={{width: RIGHT_AXIS_GUTTER, flexShrink: 0}}/>
            </Box>
            {/* The legend, centred under the whole canvas rather than under the plot — which is where
                Chart.js puts it. Each entry is a dot and a label, because the real legend is drawn with
                `usePointStyle` circles beside its text, not bare words. */}
            <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, pt: 1.25}}>
                {[0, 1, 2].map(i => (
                    <Box key={i} sx={{display: 'flex', alignItems: 'center', gap: 0.6}}>
                        <Skeleton
                            variant="circular"
                            aria-hidden="true"
                            data-testid="legend-dot-skeleton"
                            sx={{width: '0.55em', height: '0.55em', bgcolor: GRAPH_SKELETON_BG, flexShrink: 0}}
                        />
                        <Block width="5em" height="0.8em" testId="legend-skeleton"/>
                    </Box>
                ))}
            </Box>
        </Box>
    </Box>
);

/**
 * The three headline totals that have not arrived — Ingested Data's header row.
 *
 * ── Why this exists at all ───────────────────────────────────────────────────
 * The card's header is a flex row of two columns: the period tabs, and the totals strip beside them. Both
 * carry `flexGrow: 1`. While the figures were in flight the strip was not rendered, so the tab column had
 * the row to itself and the four tabs stretched the full width of the card; when the totals landed the strip
 * claimed its share and the tabs snapped back to about a third of it. Every element below moved with them.
 * Reserving the strip's geometry is what holds the tabs at their finished width from the first frame.
 *
 * ── The box model is copied, deliberately ────────────────────────────────────
 * `px`, `py`, `borderRadius`, `flexGrow`, `flexBasis` and `minWidth` are the same values totalsStrip() sets
 * in waterfallGraph.jsx, and each of the three text lines is reserved at the line box its Typography
 * occupies — 0.7rem for the label, 1.3rem at lineHeight 1.2 for the figure, 0.7rem for the change. That is
 * what makes the header exactly as tall while loading as it is afterwards, so the plot beneath it is not
 * handed a different height in the two states. If those values change there, change them here.
 *
 * The border is the shimmer grey rather than each series' own colour, which is what the real boxes are drawn
 * in. Colouring them would mean guessing which series arrive, and the guess is wrong for a tape-only
 * customer — they have no Files Count, so the middle box would shimmer salmon and then vanish.
 *
 * @param {number} [count=3] - how many boxes to reserve. Three is the design's full complement and the usual
 *   case; a tape-only customer resolves to two, which is a narrower jump than the whole strip appearing.
 */
export const TotalsStripSkeleton = ({count = 3}) => (
    <Box
        data-testid="totals-strip-skeleton"
        aria-hidden="true"
        sx={{display: 'flex', flexWrap: 'wrap', gap: 1, flexGrow: 1, justifyContent: 'flex-end'}}
    >
        {Array.from({length: count}, (_, i) => (
            <Box
                key={i}
                data-testid="total-box-skeleton"
                sx={{
                    border: `1px solid ${GRAPH_SKELETON_BG}`,
                    borderRadius: 1,
                    px: 1.25,
                    py: 0.25,
                    flexGrow: 1,
                    flexBasis: 0,
                    minWidth: '7.5em',
                }}
            >
                {/* One row per line of the finished box, each the height of the line it stands in for, with
                    the block centred inside it — so the reserved height is the real height. */}
                {[
                    {line: '1.05rem', width: '4.5em', height: '0.6em'},
                    {line: '1.56rem', width: '3.2em', height: '1.05em'},
                    {line: '1.05rem', width: '2.6em', height: '0.6em'},
                ].map(({line, width, height}, row) => (
                    <Box key={row} sx={{height: line, display: 'flex', alignItems: 'center'}}>
                        <Block width={width} height={height}/>
                    </Box>
                ))}
            </Box>
        ))}
    </Box>
);

/**
 * A donut that has not arrived — both halves of Backup Data and Storage Policies.
 *
 * Drawn as a filled circle with a disc of the card colour over the middle, which is how you get a ring
 * that still shimmers. Sized off the shorter side (`min(60%, 11em)` via aspectRatio) so it stays round
 * in a card that is wider than it is tall, which these are.
 */
export const DonutChartSkeleton = () => (
    <Box
        data-testid="donut-chart-skeleton"
        sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            minHeight: 0,
            gap: 1.5,
        }}
    >
        <Box sx={{position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
            <Skeleton
                variant="circular"
                aria-hidden="true"
                data-testid="donut-ring-skeleton"
                sx={{
                    width: '9em',
                    height: '9em',
                    bgcolor: GRAPH_SKELETON_BG,
                }}
            />
            {/* The hole. Not a Skeleton — it is the card showing through, and shimmering it would read
                as a second, smaller pie. */}
            <Box
                aria-hidden="true"
                sx={{
                    position: 'absolute',
                    width: '4.6em',
                    height: '4.6em',
                    borderRadius: '50%',
                    bgcolor: CARD_SURFACE,
                }}
            />
        </Box>
        {/* Where DonutCenter's total and the legend go. */}
        <Box sx={{display: 'flex', justifyContent: 'center', gap: 1.5}}>
            {[0, 1, 2].map(i => <Block key={i} width="4em" height="0.8em" testId="legend-skeleton"/>)}
        </Box>
    </Box>
);
